import * as React from 'react';
import { styled, useTheme, css } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
import { ChevronLeft, ChevronRight } from '@volkswagen-onehub/icons-core';
import { Tab } from './tab';
import { usePreviousNextButtonsDisabledState } from './use-previous-next-button-disabled-state';
import { useAnimatedIndicator } from './use-animated-indicator';
import { createA11yOutlineStyles } from '../helpers/a11y';
import { useThrottleCallback } from '@react-hook/throttle';
const StyledTabsWrapper = styled.div.withConfig({ displayName: "StyledTabsWrapper", componentId: "sc-62va80" }) `display:flex;position:relative;gap:${({ theme }) => theme.size.static250};min-height:${({ theme }) => theme.size.static535};isolation:isolate;`;
const StyledChevronLeft = styled(ChevronLeft).withConfig({ displayName: "StyledChevronLeft", componentId: "sc-111j90j" }) ``;
const StyledChevronRight = styled(ChevronRight).withConfig({ displayName: "StyledChevronRight", componentId: "sc-1g6f5ti" }) ``;
const BackgroundGradientLeft = css `inset-inline-end:0;::before{content:'';background:${({ theme }) => theme.direction === 'rtl'
    ? theme.colors.scrolling.left
    : theme.colors.scrolling.right};inset-inline-end:100%;}`;
const BackgroundGradientRight = css `inset-inline-start:0;::after{content:'';background:${({ theme }) => theme.direction === 'rtl'
    ? theme.colors.scrolling.right
    : theme.colors.scrolling.left};inset-inline-start:100%;}`;
const StyledScrollButton = styled.button.withConfig({ displayName: "StyledScrollButton", componentId: "sc-1s47bbc" }) `position:absolute;color:${({ theme }) => theme.colors.button.tertiary.default};width:${({ theme }) => theme.size.static350};padding:0;border:0;box-shadow:none;background-color:${({ theme }) => theme.colors.background.primary};opacity:1;cursor:pointer;inset-block:1em;transition:${({ theme }) => {
    const duration = theme.animation.duration.layer;
    const timingFunction = theme.animation.timingFunction.in;
    return `opacity ${duration} ${timingFunction}`;
}};${({ scrollDirection }) => scrollDirection === 'forward' && BackgroundGradientLeft} ${({ scrollDirection }) => scrollDirection === 'backward' && BackgroundGradientRight}:hover{color:${({ theme }) => theme.colors.tab.hover};}:focus-visible{${({ theme }) => createA11yOutlineStyles(theme)};}::before,::after{position:absolute;width:${({ theme }) => theme.size.static530};height:100%;top:0;bottom:0;pointer-events:none;cursor:auto;}${StyledChevronLeft},${StyledChevronRight}{opacity:1;transition:${({ theme }) => {
    const duration = theme.animation.duration.layer;
    const timingFunction = theme.animation.timingFunction.in;
    return `opacity ${duration} ${timingFunction}`;
}};transition-delay:${({ theme }) => theme.animation.duration.layer};}:disabled{transition-delay:${({ theme }) => theme.animation.duration.layer};opacity:0;cursor:auto;pointer-events:none;${StyledChevronLeft},${StyledChevronRight}{opacity:0;transition-delay:0s;}}`;
const StyledTabPanel = styled.div.withConfig({ displayName: "StyledTabPanel", componentId: "sc-jle9ug" }) `color:${({ theme }) => theme.colors.content.primary};margin:${({ theme }) => theme.size.static150} ${({ theme }) => parseInt(theme.size.static100, 10) - 2}px;:focus-visible{border-radius:${({ theme }) => theme.size.line.default};${({ theme }) => createA11yOutlineStyles(theme, 'solid')};}`;
const tabsIndicatorBackground = css `${({ theme, isCurrentActiveTabDisabled }) => isCurrentActiveTabDisabled
    ? theme.colors.tab.disabled
    : theme.colors.tab.active}`;
const StyledTabBar = styled.div.withConfig({ displayName: "StyledTabBar", componentId: "sc-1qs7pda" }) `position:${({ scrollable }) => (scrollable ? 'absolute' : 'initial')};display:flex;justify-items:start;column-gap:${({ theme }) => theme.size.static400};width:100%;z-index:-1;margin-block-start:1rem;margin-block-end:1rem;border-top:${({ tabBarVerticalPosition, variant, theme }) => variant === 'underlined' && tabBarVerticalPosition === 'top'
    ? `${theme.size.line.default} solid ${theme.colors.border.default}`
    : '0'};border-bottom:${({ tabBarVerticalPosition, variant, theme }) => variant === 'underlined' && tabBarVerticalPosition === 'bottom'
    ? `${theme.size.line.default} solid ${theme.colors.border.default}`
    : '0'};overflow-x:auto;overflow-y:hidden;scroll-behavior:smooth;overscroll-behavior-x:contain;overscroll-behavior-y:contain;::-webkit-scrollbar{display:none;}scrollbar-width:none;-webkit-overflow-scrolling:touch;${({ variant, tabBarVerticalPosition, theme, scrollable }) => {
    if (variant === 'step navigation') {
        return `padding: 0 ${theme.size.static100};`;
    }
    const topBottomValue = scrollable ? 0 : theme.size.static250;
    return css `
			&::before {
				transition: transform ${theme.animation.timingFunction.cubicOut}
					${theme.animation.duration.tabIndicator};
				width: var(--tabs-indicator-width);
				transform-origin: ${theme.direction === 'rtl' ? 'right' : 'left'};
				content: '';
				position: absolute;
				transform: var(--tabs-indicator-transform);
				height: ${theme.size.line.active};
				background: var(
					--tabs-indicator-background,
					${tabsIndicatorBackground}
				);

				${tabBarVerticalPosition}: ${topBottomValue};
			}

			// changes the color of the indicator line when the currently active tab is focused
			:has(:focus-visible)::before {
				background: var(
					--tabs-indicator-background,
					${tabsIndicatorBackground}
				);
			}
		`;
}}`;
export const Tabs = (props) => {
    const { scrollable = false, variant = 'underlined', tabBarVerticalPosition = 'bottom', defaultIndex, children, idPrefix, onChange = () => undefined, } = props;
    const commonProps = getCommonProps(props);
    const tabsRef = React.useRef([]);
    const tabBarRef = React.useRef(null);
    const [activeIndex, setActiveIndex] = React.useState(defaultIndex !== null && defaultIndex !== void 0 ? defaultIndex : 0);
    const disabledTabIndex = children.findIndex((child, index) => child.disabled === true && index === activeIndex);
    const { direction, size } = useTheme();
    const { updateIndicatorPosition, updateIndicatorColor } = useAnimatedIndicator(tabBarRef, tabsRef, activeIndex);
    const { isPreviousButtonDisabled, isNextButtonDisabled } = usePreviousNextButtonsDisabledState({
        tabBarRef,
        tabsRef,
    });
    const scrollToTab = React.useCallback((index) => {
        if (!tabBarRef.current || !tabsRef.current || !tabsRef.current[index]) {
            return;
        }
        const scrollLeft = tabBarRef.current.scrollLeft;
        const { left: tabBarLeft, right: tabBarRight } = tabBarRef.current.getBoundingClientRect();
        const { left: targetTabLeft, right: targetTabRight } = tabsRef.current[index].getBoundingClientRect();
        const navigationShadowWidth = parseInt(size.static530, 10);
        // divided by 2 because it is enough to have the clearly visible tab
        const arrowButtonWidth = parseInt(size.static350, 10) / 2;
        if (targetTabLeft <
            tabBarLeft + navigationShadowWidth + arrowButtonWidth) {
            const left = scrollLeft +
                targetTabLeft -
                (navigationShadowWidth + arrowButtonWidth) -
                tabBarLeft;
            tabBarRef.current.scrollTo({
                left,
                behavior: 'smooth',
            });
            return;
        }
        if (targetTabRight + navigationShadowWidth + arrowButtonWidth >
            tabBarRight) {
            const left = scrollLeft +
                navigationShadowWidth +
                arrowButtonWidth +
                targetTabRight -
                tabBarRight;
            tabBarRef.current.scrollTo({
                left,
                behavior: 'smooth',
            });
        }
    }, [size]);
    const setFocusToActiveTab = React.useCallback((activeIndex) => {
        const activeTabDOMNode = tabsRef.current[activeIndex];
        if (activeTabDOMNode) {
            activeTabDOMNode.focus();
        }
    }, []);
    const handleScroll = useThrottleCallback(React.useCallback(() => {
        updateIndicatorPosition(activeIndex);
    }, [updateIndicatorPosition, activeIndex]), 60, true);
    const setActiveTab = React.useCallback((index, isFirstLoad = false) => {
        setActiveIndex(index);
        requestAnimationFrame(() => scrollToTab(index));
        const isDisabledTab = tabsRef.current[index].getAttribute('aria-disabled') === 'true';
        // if the currently active tab is disabled choose the disabled color, otherwise tab.default
        // if the function is called after the first load change it to tab.active
        updateIndicatorColor(isDisabledTab, !isFirstLoad);
        if (!isFirstLoad) {
            setFocusToActiveTab(index);
            onChange(index);
        }
    }, [onChange, scrollToTab, setFocusToActiveTab, updateIndicatorColor, tabsRef]);
    React.useEffect(() => {
        // second parameter set to false as this would jump to component on load
        setActiveTab(defaultIndex !== null && defaultIndex !== void 0 ? defaultIndex : 0, true);
    }, [defaultIndex, setActiveTab]);
    const handleMouseEnter = React.useCallback((event, isActive) => {
        const currentlyHoveredTab = event.currentTarget;
        const currentlyHoveredTabWidth = currentlyHoveredTab.offsetWidth;
        // sets the indicator width of the currently hovered tab
        currentlyHoveredTab.style.setProperty('--tabs-indicator-width', `${currentlyHoveredTabWidth}px`);
        if (disabledTabIndex !== -1) {
            updateIndicatorColor(true);
            return;
        }
        const currentlySelectedTab = tabsRef.current.find((tab) => tab.getAttribute('aria-selected') === 'true');
        //if the current tab is selected but the hover is on another tab keep the active color on the currently selected tab
        updateIndicatorColor(false, isActive || currentlySelectedTab === document.activeElement);
    }, [updateIndicatorColor, disabledTabIndex]);
    const handleMouseLeave = React.useCallback((event) => {
        updateIndicatorColor(disabledTabIndex !== -1, event.currentTarget === document.activeElement);
    }, [updateIndicatorColor, disabledTabIndex]);
    const handleKeyUp = React.useCallback((event) => {
        event.preventDefault();
        const isArrowLeft = event.key === 'ArrowLeft';
        const isArrowRight = event.key === 'ArrowRight';
        const isLeftDirection = (isArrowLeft && direction !== 'rtl') ||
            (isArrowRight && direction === 'rtl');
        const isRightDirection = (isArrowRight && direction !== 'rtl') ||
            (isArrowLeft && direction === 'rtl');
        if (isLeftDirection) {
            const previousIndex = activeIndex > 0 ? activeIndex - 1 : tabsRef.current.length - 1;
            setActiveTab(previousIndex);
        }
        if (isRightDirection) {
            const nextIndex = activeIndex < tabsRef.current.length - 1 ? activeIndex + 1 : 0;
            setActiveTab(nextIndex);
        }
    }, [activeIndex, direction, setActiveTab]);
    const handleTabBarFocus = (e) => {
        const target = e.target;
        const isDisabledTab = target.getAttribute('aria-disabled') === 'true';
        updateIndicatorColor(isDisabledTab, true);
    };
    const handleTabBarBlur = () => updateIndicatorColor(disabledTabIndex !== -1);
    const handleChevronBackwardClick = React.useCallback(() => {
        if (!tabBarRef.current) {
            return;
        }
        const { left: tabBarLeft, right: tabBarRight } = tabBarRef.current.getBoundingClientRect();
        // find first tab (on the previous side) that is not completely visible
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- findLastIndex is missing in he types, but it exists in all supported browsers
        const previousIndex = tabsRef.current.findLastIndex((tab) => {
            const { left: tabLeft, right: tabRight } = tab.getBoundingClientRect();
            if (direction === 'rtl') {
                return Math.floor(tabRight) > Math.ceil(tabBarRight);
            }
            else {
                return Math.ceil(tabLeft) < Math.floor(tabBarLeft);
            }
        });
        if (previousIndex !== -1) {
            scrollToTab(previousIndex);
        }
    }, [direction, scrollToTab, tabBarRef]);
    const handleChevronForwardClick = React.useCallback(() => {
        if (!tabBarRef.current) {
            return;
        }
        const { left: tabBarLeft, right: tabBarRight } = tabBarRef.current.getBoundingClientRect();
        // find first tab (on the next side) that is not completely visible
        const nextTabIndex = tabsRef.current.findIndex((tab) => {
            const { left: tabLeft, right: tabRight } = tab.getBoundingClientRect();
            if (direction === 'rtl') {
                return Math.ceil(tabLeft) < Math.floor(tabBarLeft);
            }
            else {
                return Math.floor(tabRight) > Math.ceil(tabBarRight);
            }
        });
        if (nextTabIndex !== -1) {
            scrollToTab(nextTabIndex);
        }
    }, [direction, scrollToTab, tabBarRef]);
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledTabsWrapper, { onKeyUp: handleKeyUp },
            scrollable && (React.createElement(StyledScrollButton, { scrollDirection: "backward", disabled: isPreviousButtonDisabled, onClick: handleChevronBackwardClick, "aria-label": direction === 'rtl'
                    ? 'scroll to next tab'
                    : 'scroll to previous tab' }, direction === 'rtl' ? (React.createElement(StyledChevronRight, { variant: "small", ariaHidden: true })) : (React.createElement(StyledChevronLeft, { variant: "small", ariaHidden: true })))),
            React.createElement(StyledTabBar, Object.assign({ onFocus: handleTabBarFocus, onBlur: handleTabBarBlur, role: "tablist", ref: tabBarRef, variant: variant, tabBarVerticalPosition: tabBarVerticalPosition, isCurrentActiveTabDisabled: disabledTabIndex !== -1, scrollable: scrollable, onScroll: handleScroll }, commonProps), children.map(({ title, key, anchorProps = {}, disabled }, index) => (React.createElement(Tab, Object.assign({ "aria-controls": disabled ? undefined : `${idPrefix}-content-${index}`, isContainer: variant === 'step navigation', key: key, ref: (instance) => {
                    if (instance) {
                        tabsRef.current[index] = instance;
                    }
                }, isSelected: activeIndex === index, idPrefix: idPrefix, index: index, title: title, disabled: disabled, onClick: setActiveTab, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, tabBarVerticalPosition: tabBarVerticalPosition }, getCommonProps(anchorProps)))))),
            scrollable && (React.createElement(StyledScrollButton, { scrollDirection: "forward", disabled: isNextButtonDisabled, onClick: handleChevronForwardClick, "aria-label": direction === 'rtl'
                    ? 'scroll to previous tab'
                    : 'scroll to next tab' }, direction === 'rtl' ? (React.createElement(StyledChevronLeft, { variant: "small", ariaHidden: true })) : (React.createElement(StyledChevronRight, { variant: "small", ariaHidden: true }))))),
        children.map((tab, index) => (React.createElement(StyledTabPanel, { "aria-hidden": tab.disabled, tabIndex: tab.disabled ? undefined : 0, role: "tabpanel", hidden: tab.disabled || index !== activeIndex, key: tab.key, id: `${idPrefix}-content-${index}`, "aria-labelledby": `${idPrefix}-tab-${index}` }, tab.content)))));
};
